import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const refreshTimerRef = useRef(null);

  const logout = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
      } catch {}
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // Use window.location to redirect outside of React Router context
    // (logout can be triggered from interceptors, timers, etc.)
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, []);

  const scheduleRefresh = useCallback((accessToken) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiresIn = (payload.exp * 1000) - Date.now();
      // Refresh 5 minutes before expiry
      const refreshIn = Math.max(expiresIn - 5 * 60 * 1000, 60000);
      refreshTimerRef.current = setTimeout(async () => {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) return logout();
        try {
          const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
          const newToken = res.data.token;
          localStorage.setItem('token', newToken);
          setToken(newToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          scheduleRefresh(newToken);
        } catch {
          logout();
        }
      }, refreshIn);
    } catch {}
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      scheduleRefresh(token);
    } else {
      setLoading(false);
    }

    // Reactive 401 interceptor: on expired access token, refresh once and retry.
    let isRefreshing = false;
    let pendingQueue = [];
    const flushQueue = (error, newToken = null) => {
      pendingQueue.forEach((p) => (error ? p.reject(error) : p.resolve(newToken)));
      pendingQueue = [];
    };

    const interceptorId = axios.interceptors.response.use(
      (resp) => resp,
      async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const url = original?.url || '';
        // Don't try to refresh on auth endpoints themselves
        if (
          status !== 401 ||
          !original ||
          original._retry ||
          url.includes('/auth/login') ||
          url.includes('/auth/refresh') ||
          url.includes('/auth/register')
        ) {
          return Promise.reject(error);
        }

        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          logout();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            pendingQueue.push({
              resolve: (newToken) => {
                original.headers['Authorization'] = `Bearer ${newToken}`;
                resolve(axios(original));
              },
              reject,
            });
          });
        }

        original._retry = true;
        isRefreshing = true;
        try {
          const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
          const newToken = res.data.token;
          localStorage.setItem('token', newToken);
          setToken(newToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          scheduleRefresh(newToken);
          flushQueue(null, newToken);
          original.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(original);
        } catch (refreshErr) {
          flushQueue(refreshErr, null);
          logout();
          return Promise.reject(refreshErr);
        } finally {
          isRefreshing = false;
        }
      }
    );

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      axios.interceptors.response.eject(interceptorId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: accessToken, refresh_token, user: userData } = response.data;
    localStorage.setItem('token', accessToken);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    setToken(accessToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    scheduleRefresh(accessToken);
  };

  const register = async (organization_name, full_name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, {
      organization_name,
      full_name,
      email,
      password
    });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
